/**
 * Read-outs the clearance cross-section derives from data it already holds:
 * the station's occupancy grid (`sectionCellsAt`), the envelope's distance
 * field (`profileDistanceField`) and the run's per-interval bins.
 *
 * Kept apart from `section` (the drawing's own geometry) because these are
 * the engineering summaries — "which side of the gauge fouls", "where along
 * the offset axis", "where along the corridor". They began as the dock's
 * Cross-section tab read-outs and are now shared with the report worker
 * through `@gis/clearance`, which prints the same tables, histograms and
 * corridor strip in both clearance PDFs. The app imports them through the
 * re-export shim `app/src/lib/clearance/sectionStats.ts`. Pure and DOM-free,
 * like the model, so each figure is assertable without a canvas.
 */
import { heightAt, offsetAt } from './section';
import type { ClearanceBin, ClearanceSectionBins, ClearanceVertex } from './types';
import { clearanceVerdict } from './verdict';

// ------------------------------------------------------------ envelope sides

/**
 * The four faces an engineer names a foul by. A gauge is any simple polygon
 * (a box, a chamfered rail envelope, a tunnel arc), so a side is not a list
 * of edges someone labelled — it is the DIRECTION the envelope faces at the
 * point nearest the return, bucketed to the dominant axis: mostly −x is the
 * left wall, mostly +y the roof, and so on. A 45° chamfer is a tie and goes
 * to the wall: on a rail gauge the chamfers are the shoulders of the
 * structure gauge, which is where a cutting face or a mast fouls from the
 * side.
 */
export type EnvelopeSide = 'left' | 'right' | 'roof' | 'floor';

/** Table order: the two walls, then roof and floor. */
export const ENVELOPE_SIDES: readonly EnvelopeSide[] = ['left', 'right', 'roof', 'floor'];

/** Code a side is stored as in a `Uint8Array` field; `NO_SIDE` = no profile. */
const SIDE_CODE: Record<EnvelopeSide, number> = { left: 0, right: 1, roof: 2, floor: 3 };
export const NO_SIDE = 255;

/** The side a direction (outward from the envelope) points to. */
export function sideOfDirection(dx: number, dy: number): EnvelopeSide {
  // Strictly greater, with a hair of tolerance, so a 45° direction — which
  // floating point puts a few ulps either way — lands on the wall every time.
  if (Math.abs(dy) > Math.abs(dx) + 1e-9) return dy > 0 ? 'roof' : 'floor';
  return dx < 0 ? 'left' : 'right';
}

/** Twice the polygon's signed area: positive for counter-clockwise (y up). */
const signedArea2 = (v: readonly ClearanceVertex[]): number => {
  let a = 0;
  for (let i = 0, j = v.length - 1; i < v.length; j = i++) a += v[j][0] * v[i][1] - v[i][0] * v[j][1];
  return a;
};

/**
 * The side of the envelope a point at (o, h) is nearest to.
 *
 * Classified by the direction from the nearest boundary point OUTWARD — for
 * a point outside, the vector to it; for a point inside, the vector away
 * from it — rather than by which edge is nearest. The two agree along an
 * edge and differ only round a corner, where "nearest edge" is a tie the
 * vertex order would break arbitrarily, and the outward direction splits the
 * corner on its bisector instead. A point exactly on the outline has no
 * direction of its own and takes its edge's outward normal.
 */
export function profileOutwardSide(vertices: readonly ClearanceVertex[], o: number, h: number): EnvelopeSide | null {
  const n = vertices.length;
  if (n < 3) return null;
  const ccw = signedArea2(vertices) > 0;
  let inside = false;
  let best = Infinity;
  let qx = 0;
  let qy = 0;
  let nx = 0;
  let ny = 0;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const [xi, yi] = vertices[i];
    const [xj, yj] = vertices[j];
    if (yi > h !== yj > h && o < ((xj - xi) * (h - yi)) / (yj - yi) + xi) inside = !inside;
    // Edge j → i (the loop's own order), so its direction is (i − j).
    const dx = xi - xj;
    const dy = yi - yj;
    const len2 = dx * dx + dy * dy;
    if (len2 <= 0) continue;
    const t = Math.max(0, Math.min(1, ((o - xj) * dx + (h - yj) * dy) / len2));
    const px = xj + t * dx;
    const py = yj + t * dy;
    const d2 = (px - o) ** 2 + (py - h) ** 2;
    if (d2 < best) {
      best = d2;
      qx = px;
      qy = py;
      // Outward normal of a CCW edge is (dy, −dx); a CW polygon flips it.
      nx = ccw ? dy : -dy;
      ny = ccw ? -dx : dx;
    }
  }
  if (!Number.isFinite(best)) return null;
  const sign = inside ? -1 : 1;
  const ox = sign * (o - qx);
  const oy = sign * (h - qy);
  return Math.hypot(ox, oy) > 1e-9 ? sideOfDirection(ox, oy) : sideOfDirection(nx, ny);
}

/**
 * `profileOutwardSide` at every cell CENTRE, in the grid's own order — the
 * companion of `profileDistanceField`, and computed once per run for the
 * same reason: the gauge is swept unchanged, so every station shares it.
 */
export function profileSideField(bins: ClearanceSectionBins, vertices: readonly ClearanceVertex[]): Uint8Array {
  const out = new Uint8Array(bins.nHeights * bins.nOffsets).fill(NO_SIDE);
  for (let row = 0; row < bins.nHeights; row++) {
    const h = heightAt(bins, row);
    for (let col = 0; col < bins.nOffsets; col++) {
      const side = profileOutwardSide(vertices, offsetAt(bins, col), h);
      if (side) out[row * bins.nOffsets + col] = SIDE_CODE[side];
    }
  }
  return out;
}

/**
 * The outline's edges grouped by the side their outward normal faces, for
 * drawing one side of the gauge in emphasis (hovering a "Where it fouls"
 * row). Same bucketing as the cells, so the highlighted edges are the ones
 * the row's returns were counted against.
 */
export function profileSideEdges(
  vertices: readonly ClearanceVertex[],
): Record<EnvelopeSide, [ClearanceVertex, ClearanceVertex][]> {
  const out: Record<EnvelopeSide, [ClearanceVertex, ClearanceVertex][]> = { left: [], right: [], roof: [], floor: [] };
  const n = vertices.length;
  if (n < 3) return out;
  const ccw = signedArea2(vertices) > 0;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const dx = vertices[i][0] - vertices[j][0];
    const dy = vertices[i][1] - vertices[j][1];
    if (dx === 0 && dy === 0) continue;
    out[sideOfDirection(ccw ? dy : -dy, ccw ? -dx : dx)].push([vertices[j], vertices[i]]);
  }
  return out;
}

// ------------------------------------------------------------ where it fouls

/** One station's two planes, as `sectionCellsAt` returns them. */
export interface SectionCells {
  count: ArrayLike<number>;
  violations: ArrayLike<number>;
}

export interface SideFoul {
  side: EnvelopeSide;
  /** Violating points counted against this side. */
  violations: number;
  /**
   * Deepest intrusion on this side, metres; null with no violation. The side
   * holding the worker's worst point reports the worker's exact figure; the
   * others the deepest violating cell CENTRE, capped at that figure (a cell
   * centre can sit up to half a cell deeper than any point in it), so the
   * headline's side is always the deepest row.
   */
  maxIntrusionM: number | null;
  /** Points in cells amber on the drawing (outside, within the outer band). */
  nearMissPoints: number;
  /**
   * Closest a non-violating occupied cell centre comes to this side, metres
   * (0 when the centre is on or just inside the line); null with no such cell.
   */
  tightestMarginM: number | null;
}

/**
 * Per-side breakdown of one station: how much fouls each face of the gauge,
 * how deep, and how close the nearest clear return comes. Every occupied
 * cell is counted once, against the side nearest its centre.
 */
export function foulsBySide(
  cells: SectionCells,
  field: ArrayLike<number>,
  sideField: ArrayLike<number>,
  outerBandM: number,
  /** The bin's own worst point (`worstOffsetM`/`worstHeightM`/`maxIntrusionM`). */
  worst: { side: EnvelopeSide | null; depthM: number | null } = { side: null, depthM: null },
): SideFoul[] {
  const acc = ENVELOPE_SIDES.map((side) => ({
    side,
    violations: 0,
    depth: -Infinity,
    nearMissPoints: 0,
    margin: Infinity,
  }));
  for (let i = 0; i < cells.count.length; i++) {
    const total = cells.count[i];
    if (!(total > 0)) continue;
    const code = sideField[i];
    if (code === NO_SIDE || code === undefined) continue;
    const a = acc[code];
    const bad = cells.violations[i];
    const d = field[i];
    if (bad > 0) {
      a.violations += bad;
      a.depth = Math.max(a.depth, Math.max(0, -d));
    } else {
      a.margin = Math.min(a.margin, Math.max(0, d));
      if (d <= outerBandM) a.nearMissPoints += total;
    }
  }
  return acc.map((a) => {
    let depth: number | null = a.violations > 0 ? a.depth : null;
    if (worst.depthM !== null && depth !== null) {
      // The worker's figure is the deepest POINT; a cell centre can sit up to
      // half a cell past it either way, so it neither beats nor undercuts it.
      depth = a.side === worst.side ? worst.depthM : Math.min(depth, worst.depthM);
    } else if (worst.depthM !== null && a.side === worst.side && a.violations === 0) {
      // The worker saw the worst point on this side but its cell fell to
      // another one (the point sits near a corner bisector): the figure the
      // headline quotes must still have a home row.
      depth = worst.depthM;
    }
    return {
      side: a.side,
      violations: a.violations,
      maxIntrusionM: depth,
      nearMissPoints: a.nearMissPoints,
      tightestMarginM: Number.isFinite(a.margin) ? a.margin : null,
    };
  });
}

/**
 * The side to emphasise: the deepest intrusion when anything intrudes, else
 * the tightest margin among the sides with a near miss, else none — so a
 * FAIL row is red, a MARGINAL row amber, and a clean station highlights
 * nothing rather than its least-clear side.
 */
export function worstSide(fouls: readonly SideFoul[]): { side: EnvelopeSide; kind: 'violation' | 'nearMiss' } | null {
  let best: SideFoul | null = null;
  for (const f of fouls) {
    if (f.maxIntrusionM === null) continue;
    if (!best || f.maxIntrusionM > (best.maxIntrusionM ?? -1)) best = f;
  }
  if (best) return { side: best.side, kind: 'violation' };
  for (const f of fouls) {
    if (f.nearMissPoints <= 0 || f.tightestMarginM === null) continue;
    if (!best || f.tightestMarginM < (best.tightestMarginM ?? Infinity)) best = f;
  }
  return best ? { side: best.side, kind: 'nearMiss' } : null;
}

// ------------------------------------------------------------ marginal histograms

export interface SectionMarginals {
  /** Violating points per offset column / per height row. */
  colViolations: Uint32Array;
  colNearMiss: Uint32Array;
  rowViolations: Uint32Array;
  rowNearMiss: Uint32Array;
  /** Tallest stacked (violation + near-miss) bar on each axis; 0 = nothing. */
  colPeak: number;
  rowPeak: number;
}

/**
 * The station's violating and near-miss points summed down each offset column
 * and across each height row — the two histograms on the drawing's axes.
 * "Near miss" uses the drawing's own amber rule (`sectionCellState`): every
 * point of a non-violating cell whose centre is within the outer band.
 */
export function sectionMarginals(
  cells: SectionCells,
  field: ArrayLike<number>,
  outerBandM: number,
  nOffsets: number,
  nHeights: number,
): SectionMarginals {
  const colViolations = new Uint32Array(nOffsets);
  const colNearMiss = new Uint32Array(nOffsets);
  const rowViolations = new Uint32Array(nHeights);
  const rowNearMiss = new Uint32Array(nHeights);
  for (let row = 0; row < nHeights; row++) {
    for (let col = 0; col < nOffsets; col++) {
      const i = row * nOffsets + col;
      const total = cells.count[i];
      if (!(total > 0)) continue;
      const bad = cells.violations[i];
      if (bad > 0) {
        colViolations[col] += bad;
        rowViolations[row] += bad;
      } else if (field[i] <= outerBandM) {
        colNearMiss[col] += total;
        rowNearMiss[row] += total;
      }
    }
  }
  let colPeak = 0;
  for (let c = 0; c < nOffsets; c++) colPeak = Math.max(colPeak, colViolations[c] + colNearMiss[c]);
  let rowPeak = 0;
  for (let r = 0; r < nHeights; r++) rowPeak = Math.max(rowPeak, rowViolations[r] + rowNearMiss[r]);
  return { colViolations, colNearMiss, rowViolations, rowNearMiss, colPeak, rowPeak };
}

// ------------------------------------------------------------ corridor strip

export type CorridorTone = 'violation' | 'nearMiss' | 'clear' | 'empty';

export interface CorridorBucket {
  /** First and last bin index (inclusive) the bucket stands for. */
  from: number;
  to: number;
  violations: number;
  /** The worst verdict tone of any bin in it. */
  tone: CorridorTone;
}

const TONE_RANK: Record<CorridorTone, number> = { empty: 0, clear: 1, nearMiss: 2, violation: 3 };

const binTone = (b: ClearanceBin): CorridorTone => {
  switch (clearanceVerdict({ violationCount: b.violationCount, scoredCount: b.pointCount, nearMiss: b.nearMiss })) {
    case 'FAIL':
      return 'violation';
    case 'MARGINAL':
      return 'nearMiss';
    case 'PASS':
      return 'clear';
    default:
      return 'empty';
  }
};

/**
 * The run's intervals as at most `maxBuckets` bars — one per interval when
 * they fit, else consecutive intervals pooled (violations summed, the worst
 * tone kept) so a 4 000-interval corridor still draws as whole-pixel bars and
 * a single failing interval inside a pooled bar still turns it red.
 */
export function corridorBuckets(bins: readonly ClearanceBin[], maxBuckets: number): CorridorBucket[] {
  const n = bins.length;
  if (n === 0) return [];
  const k = Math.max(1, Math.min(n, Math.floor(maxBuckets)));
  const out: CorridorBucket[] = [];
  for (let b = 0; b < k; b++) {
    const from = Math.floor((b * n) / k);
    const to = Math.floor(((b + 1) * n) / k) - 1;
    let violations = 0;
    let tone: CorridorTone = 'empty';
    for (let i = from; i <= to; i++) {
      violations += bins[i].violationCount;
      const t = binTone(bins[i]);
      if (TONE_RANK[t] > TONE_RANK[tone]) tone = t;
    }
    out.push({ from, to, violations, tone });
  }
  return out;
}

/**
 * Bar height 0..1 on a log curve, the section's own density rule (`density`):
 * one run's intervals range from a single intruding leaf to a million-point
 * cutting face, and on a linear scale every interval but the worst is a
 * one-pixel stub.
 */
export const corridorHeight = (violations: number, peak: number): number =>
  violations <= 0 || peak <= 0 ? 0 : Math.log1p(violations) / Math.log1p(peak);

/** The interval under a pointer at fraction `f` (0..1) of the strip's width. */
export const binAtFraction = (nBins: number, f: number): number =>
  Math.max(0, Math.min(nBins - 1, Math.floor(Math.max(0, Math.min(1, f)) * nBins)));

/** How many intervals of the run hold a violation / only a near miss. */
export function corridorCounts(bins: readonly ClearanceBin[]): { violating: number; marginal: number } {
  let violating = 0;
  let marginal = 0;
  for (const b of bins) {
    const t = binTone(b);
    if (t === 'violation') violating++;
    else if (t === 'nearMiss') marginal++;
  }
  return { violating, marginal };
}

// ------------------------------------------------------------ worst intervals

export interface RankedInterval {
  index: number;
  kind: 'violation' | 'nearMiss';
  /** For a near miss: the tightest band any of its points reached, metres. */
  bandM: number | null;
  /** Violating points, or the points inside `bandM`. */
  count: number;
}

/**
 * The run's intervals worth visiting first, at most `n`: the deepest
 * intrusions (ties by violating points), then — when fewer than `n` intervals
 * fail — the closest near misses, tightest band first, most points within it
 * next. A clean interval never ranks: the list is "where to go", not a sort.
 */
export function rankIntervals(
  bins: readonly ClearanceBin[],
  bandsM: readonly number[],
  n: number,
): RankedInterval[] {
  if (n <= 0) return [];
  const fails = bins
    .map((b, index) => ({ b, index }))
    .filter(({ b }) => b.violationCount > 0)
    .sort(
      (x, y) =>
        (y.b.maxIntrusionM ?? 0) - (x.b.maxIntrusionM ?? 0) ||
        y.b.violationCount - x.b.violationCount ||
        x.index - y.index,
    )
    .map(({ b, index }): RankedInterval => ({ index, kind: 'violation', bandM: null, count: b.violationCount }));
  if (fails.length >= n) return fails.slice(0, n);
  const rungs = ['within0_1M', 'within0_2M', 'within0_35M'] as const;
  const near = bins
    .map((b, index) => {
      if (b.violationCount > 0) return null;
      const r = rungs.findIndex((k) => b.nearMiss[k] > 0);
      return r < 0 ? null : { index, r, count: b.nearMiss[rungs[r]] };
    })
    .filter((x): x is { index: number; r: number; count: number } => x !== null)
    .sort((x, y) => x.r - y.r || y.count - x.count || x.index - y.index)
    .map(
      ({ index, r, count }): RankedInterval => ({
        index,
        kind: 'nearMiss',
        bandM: bandsM[r] ?? bandsM[bandsM.length - 1] ?? null,
        count,
      }),
    );
  return [...fails, ...near].slice(0, n);
}
